import { useEffect, useMemo, useRef, useState } from 'react';
import { get, post, session, fmt, ApiError } from '../lib/api';
import { enqueue, flush } from '../lib/outbox';
import TenderModal from '../components/TenderModal';
import ReceiptModal from '../components/ReceiptModal';

interface Hit {
  variantId: string; sku: string; barcode: string | null; size: string | null;
  colour: string | null; priceMinor: number; vatRateBp: number;
  productId: string; titleEn: string; titleAr: string | null;
  styleCode: string | null; imageUrl: string | null; category: string | null;
  stock: Record<string, number>; availableHere: number | null;
}

export interface CartLine {
  key: string;
  variantId: string;
  sku: string;
  title: string;
  size: string | null;
  quantity: number;
  unitGrossMinor: number;
  discountMinor: number;
  vatRateBp: number;
  availableHere: number | null;
}

const CART_KEY = 'femi9.cart';
const PARKED_KEY = 'femi9.parked';

/** VAT is inclusive: net and VAT are derived from the tag price, never added. */
function vatFromGross(gross: number, rateBp = 1000) {
  const vat = Math.round((gross * rateBp) / (10000 + rateBp));
  return { vat, net: gross - vat };
}

export default function Till() {
  const s = session.get()!;
  const [query, setQuery] = useState('');
  const [hits, setHits] = useState<Hit[]>([]);
  const [matchType, setMatchType] = useState('');
  const [searching, setSearching] = useState(false);
  const [lines, setLines] = useState<CartLine[]>(() => {
    try { return JSON.parse(localStorage.getItem(CART_KEY) ?? '[]'); } catch { return []; }
  });
  const [orderDiscount, setOrderDiscount] = useState(0);
  const [sizeGrid, setSizeGrid] = useState<{ title: string; variants: any[] } | null>(null);
  const [tenderOpen, setTenderOpen] = useState(false);
  const [receipt, setReceipt] = useState<any>(null);
  const [toast, setToast] = useState<{ kind: 'ok' | 'err'; msg: string } | null>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  // Cart survives reload and crash — a parked basket must never evaporate.
  useEffect(() => { localStorage.setItem(CART_KEY, JSON.stringify(lines)); }, [lines]);

  useEffect(() => { searchRef.current?.focus(); }, []);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 3500);
    return () => clearTimeout(t);
  }, [toast]);

  const totals = useMemo(() => {
    const subtotal = lines.reduce((sum, l) => sum + l.quantity * l.unitGrossMinor, 0);
    const lineDisc = lines.reduce((sum, l) => sum + l.discountMinor, 0);
    const gross = Math.max(0, subtotal - lineDisc - orderDiscount);
    const { vat, net } = vatFromGross(gross);
    const payableCash = Math.round(gross / 5) * 5;
    return { subtotal, discount: lineDisc + orderDiscount, gross, vat, net, payableCash, rounding: payableCash - gross };
  }, [lines, orderDiscount]);

  const units = lines.reduce((n, l) => n + l.quantity, 0);

  async function runSearch(q: string) {
    if (!q.trim()) { setHits([]); setMatchType(''); return; }
    setSearching(true);
    try {
      const data = await get<any>(`/api/v1/lookup?q=${encodeURIComponent(q)}&storeCode=${s.storeCode}&limit=40`);
      setHits(data.results);
      setMatchType(data.matchType);
      // A barcode scan is unambiguous: add it straight to the cart. That single
      // behaviour is most of what makes a till feel fast.
      if (data.matchType === 'barcode' && data.count === 1) {
        addToCart(data.results[0]);
        setQuery('');
        setHits([]);
      }
    } catch (err) {
      setToast({ kind: 'err', msg: err instanceof ApiError ? err.message : 'Search failed' });
    } finally {
      setSearching(false);
    }
  }

  // Debounced typing; Enter forces an immediate lookup (scanners send Enter).
  useEffect(() => {
    if (!query.trim()) { setHits([]); return; }
    const t = setTimeout(() => void runSearch(query), 220);
    return () => clearTimeout(t);
  }, [query]);

  function addToCart(h: Hit) {
    if (h.priceMinor <= 0) {
      setToast({ kind: 'err', msg: `${h.sku} has no price set — fix it in the back office before selling` });
      return;
    }
    setLines((prev) => {
      const existing = prev.find((l) => l.variantId === h.variantId && l.discountMinor === 0);
      if (existing) {
        return prev.map((l) => l === existing ? { ...l, quantity: l.quantity + 1 } : l);
      }
      return [...prev, {
        key: `${h.variantId}-${Date.now()}`,
        variantId: h.variantId,
        sku: h.sku,
        title: h.titleEn,
        size: h.size,
        quantity: 1,
        unitGrossMinor: h.priceMinor,
        discountMinor: 0,
        vatRateBp: h.vatRateBp,
        availableHere: h.availableHere,
      }];
    });
  }

  async function openSizeGrid(productId: string) {
    try {
      const data = await get<any>(`/api/v1/products/${productId}`);
      setSizeGrid({ title: data.product.titleEn, variants: data.variants });
    } catch {
      setToast({ kind: 'err', msg: 'Could not load the size grid' });
    }
  }

  function setQty(key: string, q: number) {
    if (q <= 0) { setLines((p) => p.filter((l) => l.key !== key)); return; }
    setLines((p) => p.map((l) => l.key === key ? { ...l, quantity: q } : l));
  }

  function park() {
    if (!lines.length) return;
    const parked = JSON.parse(localStorage.getItem(PARKED_KEY) ?? '[]');
    parked.push({ at: new Date().toISOString(), lines, orderDiscount, by: s.user.name });
    localStorage.setItem(PARKED_KEY, JSON.stringify(parked));
    setLines([]); setOrderDiscount(0);
    setToast({ kind: 'ok', msg: 'Basket parked' });
  }

  const parked: any[] = JSON.parse(localStorage.getItem(PARKED_KEY) ?? '[]');

  function retrieve(i: number) {
    const all = JSON.parse(localStorage.getItem(PARKED_KEY) ?? '[]');
    const item = all[i];
    if (!item) return;
    setLines(item.lines); setOrderDiscount(item.orderDiscount ?? 0);
    all.splice(i, 1);
    localStorage.setItem(PARKED_KEY, JSON.stringify(all));
  }

  /**
   * Complete the sale. The document is QUEUED FIRST and the receipt is rendered
   * from local data, so the customer is never waiting on the network and a
   * connection drop cannot lose a sale that has already been paid for.
   */
  async function completeSale(payments: any[]) {
    const id = crypto.randomUUID();
    const payload = {
      id,
      storeCode: s.storeCode,
      terminalCode: s.terminalCode,
      lines: lines.map((l) => ({
        variantId: l.variantId, quantity: l.quantity, unitGrossMinor: l.unitGrossMinor,
        discountMinor: l.discountMinor, vatRateBp: l.vatRateBp,
      })),
      orderDiscountMinor: orderDiscount,
      payments,
      deviceTime: new Date().toISOString(),
    };

    enqueue('sale', id, payload);

    const localReceipt = {
      id,
      receiptNo: 'PENDING',
      lines, totals, payments,
      storeCode: s.storeCode, terminalCode: s.terminalCode,
      cashier: s.user.name,
      at: new Date().toISOString(),
      synced: false,
    };

    setLines([]); setOrderDiscount(0); setTenderOpen(false);
    setReceipt(localReceipt);

    // Best-effort immediate send so the real receipt number appears if online.
    const res = await flush();
    if (res.sent > 0) {
      try {
        const sale = await get<any>(`/api/v1/sales/${id}`);
        setReceipt((r: any) => r && r.id === id
          ? { ...r, receiptNo: sale.sale.receiptNo, synced: true } : r);
      } catch { /* receipt stays PENDING; it will sync */ }
    }
    searchRef.current?.focus();
  }

  return (
    <div className="h-[calc(100vh-49px)] flex">
      {/* ─────────── Left: search + results ─────────── */}
      <div className="flex-1 min-w-0 flex flex-col p-4 gap-3">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <input
              ref={searchRef}
              className="input w-full h-12 text-base pl-10"
              placeholder="Scan barcode, or search SKU / style / name…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') void runSearch(query); }}
            />
            <span className="absolute left-3 top-3.5 text-gray-500">⌕</span>
            {searching && <span className="absolute right-3 top-3.5 text-xs text-gray-500">…</span>}
          </div>
          {parked.length > 0 && (
            <button className="btn-ghost h-12" onClick={() => retrieve(parked.length - 1)}>
              Retrieve ({parked.length})
            </button>
          )}
        </div>

        {matchType && hits.length > 0 && (
          <div className="text-xs text-gray-500">
            {hits.length} result{hits.length === 1 ? '' : 's'} · matched by {matchType}
          </div>
        )}

        <div className="flex-1 overflow-y-auto">
          {hits.length === 0 && !query && (
            <div className="h-full flex items-center justify-center text-center">
              <div className="text-gray-600">
                <div className="text-5xl mb-3">⌗</div>
                <div className="text-sm">Scan a barcode to begin</div>
                <div className="text-xs mt-1 text-gray-700">or type a SKU, style code or product name</div>
              </div>
            </div>
          )}
          <div className="grid grid-cols-2 xl:grid-cols-3 gap-2">
            {hits.map((h) => {
              const here = h.availableHere ?? 0;
              const elsewhere = Object.entries(h.stock)
                .filter(([code, q]) => code !== s.storeCode && q > 0);
              return (
                <div key={h.variantId} className="card p-3 flex gap-3 hover:border-gray-700">
                  {h.imageUrl
                    ? <img src={h.imageUrl} alt="" className="w-14 h-16 object-cover rounded bg-gray-800 shrink-0" />
                    : <div className="w-14 h-16 rounded bg-gray-800 shrink-0" />}
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate" title={h.titleEn}>{h.titleEn}</div>
                    <div className="text-[11px] text-gray-500 truncate">{h.sku}</div>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="money text-sm font-semibold">{fmt(h.priceMinor)}</span>
                      {h.size && <span className="badge bg-gray-800 text-gray-300">{h.size}</span>}
                    </div>
                    <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                      <span className={`badge ${here > 0 ? 'bg-emerald-900/50 text-emerald-300' : 'bg-red-900/40 text-red-300'}`}>
                        {here > 0 ? `${here} here` : 'None here'}
                      </span>
                      {/* Cross-store visibility is a revenue feature: "we have it at City Centre". */}
                      {elsewhere.map(([code, q]) => (
                        <span key={code} className="badge bg-sky-900/40 text-sky-300">{q} @ {code}</span>
                      ))}
                      {!h.barcode && <span className="badge bg-amber-900/40 text-amber-300">No barcode</span>}
                    </div>
                    <div className="flex gap-1.5 mt-2">
                      <button className="btn-primary text-xs py-1 px-2.5" onClick={() => addToCart(h)}>Add</button>
                      <button className="btn-ghost text-xs py-1 px-2.5" onClick={() => void openSizeGrid(h.productId)}>Sizes</button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* ─────────── Right: cart ─────────── */}
      <div className="w-[420px] shrink-0 border-l border-gray-800 bg-gray-900/40 flex flex-col">
        <div className="px-4 py-3 border-b border-gray-800 flex items-center justify-between">
          <div className="font-medium">Basket</div>
          <div className="text-xs text-gray-500">{units} unit{units === 1 ? '' : 's'}</div>
        </div>

        <div className="flex-1 overflow-y-auto p-3 space-y-2">
          {lines.length === 0 && (
            <div className="text-center text-gray-600 text-sm py-10">Basket is empty</div>
          )}
          {lines.map((l) => (
            <div key={l.key} className="card p-2.5">
              <div className="flex justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-sm truncate">{l.title}</div>
                  <div className="text-[11px] text-gray-500">
                    {l.sku}{l.size ? ` · ${l.size}` : ''}
                  </div>
                </div>
                <button className="text-gray-500 hover:text-red-400 text-lg leading-none px-1"
                  onClick={() => setQty(l.key, 0)} title="Remove">×</button>
              </div>
              <div className="flex items-center justify-between mt-2">
                <div className="flex items-center gap-1">
                  <button className="w-7 h-7 rounded bg-gray-800 hover:bg-gray-700" onClick={() => setQty(l.key, l.quantity - 1)}>−</button>
                  <input
                    className="w-11 h-7 text-center bg-gray-950 border border-gray-700 rounded money text-sm"
                    value={l.quantity}
                    onChange={(e) => setQty(l.key, Math.max(0, Number(e.target.value) || 0))}
                  />
                  <button className="w-7 h-7 rounded bg-gray-800 hover:bg-gray-700" onClick={() => setQty(l.key, l.quantity + 1)}>+</button>
                  {l.availableHere !== null && l.quantity > l.availableHere && (
                    <span className="badge bg-amber-900/40 text-amber-300 ml-1" title="Selling more than the system shows in stock">
                      over stock
                    </span>
                  )}
                </div>
                <div className="money text-sm font-semibold">
                  {fmt(l.quantity * l.unitGrossMinor - l.discountMinor)}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="border-t border-gray-800 p-4 space-y-1.5 text-sm">
          <Row label="Subtotal" value={fmt(totals.subtotal)} />
          {totals.discount > 0 && <Row label="Discount" value={`−${fmt(totals.discount)}`} accent="text-amber-300" />}
          <Row label={`VAT 10% (included)`} value={fmt(totals.vat)} muted />
          {totals.rounding !== 0 && (
            <Row label="Cash rounding (5 fils)" value={`${totals.rounding > 0 ? '+' : ''}${totals.rounding}`} muted />
          )}
          <div className="flex justify-between items-baseline pt-2 mt-1 border-t border-gray-800">
            <span className="font-medium">Total</span>
            <span className="money text-2xl font-bold">{fmt(totals.payableCash)}</span>
          </div>

          <div className="grid grid-cols-2 gap-2 pt-3">
            <button className="btn-ghost" disabled={!lines.length} onClick={park}>Park</button>
            <button className="btn-ghost" disabled={!lines.length}
              onClick={() => { setLines([]); setOrderDiscount(0); }}>Clear</button>
          </div>
          <button className="btn-primary w-full h-12 text-base mt-1"
            disabled={!lines.length} onClick={() => setTenderOpen(true)}>
            Pay · {fmt(totals.payableCash)}
          </button>
        </div>
      </div>

      {/* ─────────── Modals ─────────── */}
      {sizeGrid && (
        <Modal onClose={() => setSizeGrid(null)} title={sizeGrid.title} width="max-w-2xl">
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
            {sizeGrid.variants.map((v: any) => {
              const here = v.stock?.[s.storeCode] ?? 0;
              return (
                <button
                  key={v.id}
                  disabled={v.priceMinor <= 0}
                  onClick={() => {
                    addToCart({
                      variantId: v.id, sku: v.sku, barcode: v.barcode, size: v.size, colour: v.colour,
                      priceMinor: v.priceMinor, vatRateBp: v.vatRateBp, productId: v.productId,
                      titleEn: sizeGrid.title, titleAr: null, styleCode: null, imageUrl: null,
                      category: null, stock: v.stock ?? {}, availableHere: here,
                    });
                    setSizeGrid(null);
                  }}
                  className={`p-3 rounded-lg border text-left ${
                    here > 0 ? 'border-gray-700 bg-gray-800 hover:border-brand' : 'border-gray-800 bg-gray-900 opacity-60'
                  }`}
                >
                  <div className="font-semibold">{v.size ?? '—'}</div>
                  <div className="money text-xs text-gray-400">{fmt(v.priceMinor)}</div>
                  <div className={`text-[11px] mt-0.5 ${here > 0 ? 'text-emerald-400' : 'text-gray-500'}`}>
                    {here} in stock
                  </div>
                </button>
              );
            })}
          </div>
        </Modal>
      )}

      {tenderOpen && (
        <TenderModal
          totals={totals}
          onClose={() => setTenderOpen(false)}
          onConfirm={completeSale}
        />
      )}

      {receipt && <ReceiptModal receipt={receipt} onClose={() => setReceipt(null)} />}

      {toast && (
        <div className={`fixed bottom-5 left-1/2 -translate-x-1/2 px-4 py-2.5 rounded-lg text-sm shadow-xl z-50 ${
          toast.kind === 'ok' ? 'bg-emerald-800 text-emerald-100' : 'bg-red-800 text-red-100'
        }`}>
          {toast.msg}
        </div>
      )}
    </div>
  );
}

function Row({ label, value, muted, accent }: { label: string; value: string; muted?: boolean; accent?: string }) {
  return (
    <div className="flex justify-between">
      <span className={muted ? 'text-gray-500' : 'text-gray-400'}>{label}</span>
      <span className={`money ${accent ?? (muted ? 'text-gray-500' : '')}`}>{value}</span>
    </div>
  );
}

export function Modal({ children, onClose, title, width = 'max-w-md' }: {
  children: React.ReactNode; onClose: () => void; title: string; width?: string;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-40" onClick={onClose}>
      <div className={`card w-full ${width} max-h-[90vh] flex flex-col`} onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-gray-800 flex items-center justify-between">
          <div className="font-medium">{title}</div>
          <button className="text-gray-500 hover:text-gray-200 text-xl leading-none" onClick={onClose}>×</button>
        </div>
        <div className="p-5 overflow-y-auto">{children}</div>
      </div>
    </div>
  );
}
